'use client';

import { useEffect, useState } from 'react';
import { Logo } from '@/components/Logo';

/**
 * A deploy changes every chunk filename. A tab that was opened beforehand — or
 * one restored from the back/forward cache — then asks for a chunk that no longer
 * exists, and the dynamic import behind the PDF viewer throws. That is a stale
 * bundle, not a broken app, and reloading fixes it, so do that once automatically
 * rather than showing the reader a dead end.
 *
 * The marker is time-boxed rather than permanent: if the reload does not help we
 * stop and show the message, but a genuine stale bundle later in the same session
 * can still self-heal.
 */
const STALE_BUNDLE = /ChunkLoadError|Loading chunk|Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Load failed/i;
const RELOAD_KEY = 'pv:stale-bundle-reload';
const RELOAD_WINDOW_MS = 30_000;

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [recovering, setRecovering] = useState(false);

  useEffect(() => {
    console.error(error);

    // Report it, so a failure we cannot reproduce locally still arrives with a
    // stack. Fire-and-forget: the reporter must never make things worse.
    try {
      void fetch('/api/client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: error.name,
          message: error.message,
          stack: error.stack,
          digest: error.digest,
          url: window.location.href,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* ignore */
    }

    const looksStale = STALE_BUNDLE.test(`${error.name} ${error.message}`);
    if (!looksStale) return;

    try {
      const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
      if (Date.now() - last < RELOAD_WINDOW_MS) return;
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    } catch {
      // Private browsing can refuse sessionStorage; reloading once is still right.
    }
    setRecovering(true);
    window.location.reload();
  }, [error]);

  if (recovering) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6">
        <p className="text-sm text-ink-500">Updating to the latest version…</p>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center px-6">
      <div className="w-full max-w-sm text-center">
        <div className="mb-5 flex justify-center">
          <Logo className="h-9 w-9" />
        </div>
        <h1 className="text-lg font-semibold text-ink-900">Something went wrong</h1>
        <p className="mt-1.5 text-sm text-ink-500">
          This page failed to load. It is usually temporary — try again.
        </p>
        <div className="mt-5 flex justify-center gap-2">
          <button
            type="button"
            onClick={reset}
            className="h-10 rounded-lg bg-brand-600 px-4 text-sm font-medium text-white hover:bg-brand-700"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="h-10 rounded-lg bg-white px-4 text-sm font-medium text-ink-800 ring-1 ring-ink-200 hover:bg-ink-50"
          >
            Reload page
          </button>
        </div>

        {/* Surfaced so a report can name the failure instead of describing it. */}
        <details className="mt-6 text-left">
          <summary className="cursor-pointer text-xs text-ink-400 hover:text-ink-600">
            Technical details
          </summary>
          <pre className="mt-2 overflow-x-auto rounded-lg bg-ink-100 p-3 text-[11px] leading-relaxed text-ink-700">
{`${error.name}: ${error.message}`}{error.digest ? `\nref: ${error.digest}` : ''}{error.stack ? `\n\n${error.stack}` : ''}
          </pre>
        </details>
      </div>
    </main>
  );
}
